import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  costFormater,
  getFullnameAbbr,
  List,
  Option,
  OptionKey,
  OverflowText,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { DEPENDERS_COLORS } from '@/modules/deductibles/SpendTracker/SpendTrackerUser';
import { EmptyTransactionsListPlaceholder } from '@/modules/transaction/components/TransactionsList/EmptyTransactionsListPlaceholder';
import theme from '@/styles/theme';

import useLoadMore from './hooks/useLoadMore';
import { LoadMoreButton } from './LoadMoreButton';
import { SpendLimitsPeriodHeader } from './SpendLimitsPeriodHeader';
import type { DependentSpendLimit } from './types';

interface SpendLimitsTableProps {
  dependents: DependentSpendLimit[];
  isLoading?: boolean;
  hideBackground?: boolean;
  showPeriodDropdown?: boolean;
  periodOptions?: Option[];
  selectedPeriod?: OptionKey;
  onPeriodChange?: (value: OptionKey) => void;
  isPeriodLoading?: boolean;
}

export const SpendLimitsTable: React.FC<SpendLimitsTableProps> = ({
  dependents,
  isLoading,
  hideBackground,
  showPeriodDropdown,
  periodOptions = [],
  selectedPeriod,
  onPeriodChange,
  isPeriodLoading,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const {
    displayedItems: displayedDependents,
    showAll,
    hasMoreItems,
    remainingCount,
    toggleShowAll,
  } = useLoadMore(dependents);
  const hasData = dependents.length > 0;

  const totalCount = useMemo(() => dependents.length, [dependents.length]);
  const content = (
    <>
      <SpendLimitsPeriodHeader
        title={`${t('Person spend limits')}: ${totalCount || '0'}`}
        showPeriodDropdown={showPeriodDropdown}
        periodOptions={periodOptions}
        selectedPeriod={selectedPeriod}
        onPeriodChange={onPeriodChange}
        isPeriodLoading={isPeriodLoading}
        hideBackground={hideBackground}
        testIdPrefix="spend-limits"
      />
      {isMobile ? (
        <Box direction="column" gap="spacing12">
          {isLoading ? (
            <List.Preloader testId="spend-limits-preloader" count={1} />
          ) : (
            <>
              <Box direction="column" gap="spacing8" style={{ position: 'relative' }}>
                {!hasData && <EmptyTransactionsListPlaceholder title={t('Person spend limits')} />}
                {displayedDependents.map((dependent, index) => {
                  const isLastItem = index === displayedDependents.length - 1;
                  const shouldShowLoader = isLastItem && showAll && isLoading;

                  return (
                    <Box
                      key={dependent.id}
                      data-testid={`spend-limit-item-${dependent.id}`}
                      border={{ side: 'all', size: 'small', color: 'border2' }}
                      background="canvas"
                      round="container2Round"
                      pad="spacing16"
                      gap="spacing8"
                    >
                      <Box direction="row" align="center" justify="between">
                        <Text weight="bold" size="large" color="textBody">
                          {dependent.name}
                        </Text>
                        {dependent.relationshipLabel && (
                          <Box
                            background="border2"
                            round="button2Round"
                            pad={{ horizontal: 'spacing6', vertical: 'spacing2' }}
                          >
                            <Text color="textBody" weight={500} size="small">
                              {dependent.relationshipLabel}
                            </Text>
                          </Box>
                        )}
                      </Box>
                      <Box alignSelf="stretch" height="1px" background="border1" />
                      <Box direction="row" justify="between">
                        <Text color="textSecondary" weight={500} size="small">
                          {t('Person spend limit')}
                        </Text>
                        <Text color="textBody" size="small" weight={500}>
                          {costFormater(dependent.personSpendLimit, true)}
                        </Text>
                      </Box>
                      <Box direction="row" justify="between">
                        <Text color="textSecondary" weight={500} size="small">
                          {t('Total spent')}
                        </Text>
                        <Text color="textBody" size="small" weight={500}>
                          {costFormater(dependent.totalSpent, true)}
                        </Text>
                      </Box>
                      {shouldShowLoader && (
                        <Box align="center" margin={{ top: 'spacing8' }}>
                          <List.Preloader testId="spend-limits-last-item-loader" count={1} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
                {hasMoreItems && !showAll && (
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '120px',
                      background: theme.colors.fade1,
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
              {hasMoreItems && (
                <Box margin={{ top: 'spacing16' }}>
                  <LoadMoreButton
                    isExpanded={showAll}
                    remainingCount={remainingCount}
                    onClick={toggleShowAll}
                    isPending={isLoading}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      ) : (
        <List>
          <List.Header style={{ border: `1px solid ${theme.colors.legend}` }}>
            <List.Header.Item flex={5}>{t('Full name')}</List.Header.Item>
            <List.Header.Item flex={2.5} margin={{ left: 's' }}>
              {t('Person spend limit')}
            </List.Header.Item>
            <List.Header.Item flex={2.5}>{t('Total spent')}</List.Header.Item>
          </List.Header>

          {isLoading ? (
            <List.Preloader testId="spend-limits-preloader" count={1} />
          ) : (
            <>
              <Box style={{ position: 'relative' }}>
                {!hasData && <EmptyTransactionsListPlaceholder title={t('Spend Limits')} />}
                {displayedDependents.map((dependent, index) => {
                  const isLastItem = index === displayedDependents.length - 1;
                  const shouldShowLoader = isLastItem && showAll && isLoading;

                  return (
                    <React.Fragment key={dependent.id}>
                      <List.Item disableHover data-testid={`spend-limit-item-${dependent.id}`}>
                        <Box style={{ flex: 5 }} direction="row" align="center" gap="spacing12">
                          <Box
                            width="32px"
                            height="32px"
                            align="center"
                            round="50%"
                            justify="center"
                            style={{ minWidth: '32px' }}
                            background={DEPENDERS_COLORS[index % DEPENDERS_COLORS.length]}
                          >
                            <Text color="iconOnColor" weight={700}>
                              {getFullnameAbbr(dependent.name)}
                            </Text>
                          </Box>
                          <Box direction="row" align="center" gap="spacing8">
                            <OverflowText title={dependent.name} textStyle={{ fontWeight: 'bold' }} />
                            {dependent.relationshipLabel && (
                              <Text weight={400} color="textSecondary">
                                ({dependent.relationshipLabel})
                              </Text>
                            )}
                          </Box>
                        </Box>
                        <List.Item.Text flex={2.5} weight={400} style={{ marginLeft: theme.spacings.s }}>
                          {costFormater(dependent.personSpendLimit, true)}
                        </List.Item.Text>
                        <List.Item.Text flex={2.5} weight={400}>
                          {costFormater(dependent.totalSpent, true)}
                        </List.Item.Text>
                      </List.Item>
                      {shouldShowLoader && (
                        <Box align="center" pad={{ vertical: 'spacing8' }}>
                          <List.Preloader testId="spend-limits-last-item-loader" count={1} />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
                {hasMoreItems && !showAll && (
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '120px',
                      background: theme.colors.fade1,
                    }}
                  />
                )}
              </Box>
              {hasMoreItems && (
                <Box pad={{ vertical: 'spacing16' }}>
                  <LoadMoreButton
                    isExpanded={showAll}
                    remainingCount={remainingCount}
                    onClick={toggleShowAll}
                    isPending={isLoading}
                  />
                </Box>
              )}
            </>
          )}
        </List>
      )}
    </>
  );

  if (hideBackground) {
    return <Box data-testid="SpendLimitsTable">{content}</Box>;
  }

  return (
    <Box data-testid="SpendLimitsTable" background="module" round="container1Round" pad="l">
      {content}
    </Box>
  );
};
